import { cn } from '@cocoonspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

export const cardVariants = cva(
	'relative rounded-lg bg-card text-card-foreground',
	{
		defaultVariants: {
			variant: 'default',
		},
		variants: {
			hoverEffect: {
				true: 'cursor-pointer transition-all duration-500 hover:shadow-[inset_0_0_0_3px_#ff4338]',
			},
			variant: {
				default: 'shadow-sm',
				outline: 'border',
			},
		},
	},
)

const Card = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>
>(({ className, variant, ...props }, ref) => (
	<div
		className={cn(cardVariants({ className, variant }))}
		ref={ref}
		{...props}
	/>
))

Card.displayName = 'Card'

const CardHeader = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		className={cn('flex flex-col space-y-1.5 p-8', className)}
		ref={ref}
		{...props}
	/>
))

CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h3
		className={cn('font-semibold text-2xl leading-none', className)}
		ref={ref}
		{...props}
	/>
))

CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p
		className={cn('text-muted-foreground text-sm', className)}
		ref={ref}
		{...props}
	/>
))

CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		className={cn('flex-1 p-8 pt-0', className)}
		ref={ref}
		{...props}
	/>
))

CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<div
		className={cn('flex items-center p-8 pt-0', className)}
		ref={ref}
		{...props}
	/>
))

CardFooter.displayName = 'CardFooter'

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle }
